/**
 * Download list - Figma `6440:36098`, card `6440:36101`.
 *
 * A sentence over a three-up grid of cards, each offering one thing to take away: a
 * download glyph, a title, a line of explanation.
 *
 * **A card points at a file or at an address, and the file wins.** An editor uploading an
 * offer picks the file; one linking to a partner's portal, or to a page that generates the
 * document, types the URL. Both are on `shared.download-item` because the design cannot
 * tell them apart and neither can the visitor - what they see either way is a card that
 * hands them something.
 *
 * **A card with neither is drawn, not dropped.** It reads as a card that has not been
 * finished, which is what it is; `publish-guard.js` refuses to publish the page until it
 * is. Silently leaving it out would have an editor add a card, save, and find nothing on
 * the page with no explanation anywhere.
 *
 * A file opens in a new tab rather than replacing the page. `download` asks the browser to
 * save it instead, which is what the icon promises - the browser is free to ignore that
 * for a type it can display, and a PDF opening in a viewer is the behaviour people expect.
 */
import { SectionShell, Eyebrow } from '../primitives';
import type { DownloadListSection, DownloadItem } from '../../types/sections';
import { Icon } from '../../components/ui/Icon';
import { AppLink } from '../../components/ui/AppLink';
import { mediaUrl } from '../../lib/config';

/** Card padding, border and radius are the same whether it is a link or not. */
const CARD = 'flex flex-col gap-4 rounded-l border border-grey-200 bg-grey-50 p-8';

function Inner({ item }: { item: DownloadItem }) {
  return (
    <>
      <Icon name="file-download" size={24} className="text-brand-500" />
      <h3 className="text-h5 text-ink">{item.title}</h3>
      {item.body ? <p className="text-body-sm text-ink-muted">{item.body}</p> : null}
    </>
  );
}

export default function DownloadList({ data }: { data: DownloadListSection }) {
  const items = data.items ?? [];

  return (
    <SectionShell settings={data.settings} name="download-list">
      {data.eyebrow ? <Eyebrow value={data.eyebrow} /> : null}
      {data.heading ? (
        <h2 className="max-w-[968px] whitespace-pre-line text-h3 text-ink">{data.heading}</h2>
      ) : null}

      {items.length ? (
        <ul className="grid list-none gap-5 p-0 md:grid-cols-2 xl:grid-cols-3">
          {items.map((item, i) => {
            const file = mediaUrl(item.file?.url);

            return (
              <li key={i}>
                {file ? (
                  <a
                    href={file}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${CARD} transition hover:border-brand-300`}
                  >
                    <Inner item={item} />
                  </a>
                ) : item.url ? (
                  <AppLink to={item.url} className={`${CARD} transition hover:border-brand-300`}>
                    <Inner item={item} />
                  </AppLink>
                ) : (
                  <div className={CARD}>
                    <Inner item={item} />
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      ) : null}
    </SectionShell>
  );
}
